import { z } from "zod";
import { ContentKeySchema } from "./wire.js";
/**
 * The template vocabulary both sites share: `content/taxonomy.yaml` (workflows/
 * V5-Template-Organization.md Rev 2.1 §R2.1.2, Rev 2 §R2.8). The file is content, so adding a
 * category, an industry or a keyword is a content edit; this module holds only its shape and
 * the few keys the rules name. The publish gate (server/src/catalog/validators.ts) checks every
 * sidecar against it.
 */
/** The vocabulary's file name, at the root of the content tree. */
export const TAXONOMY_FILE = "taxonomy.yaml";
/**
 * The keyword (a sidecar's `tags`) that marks the templates carried over from before
 * multi-agent support. It was a category until the template-method review (D1): an Original
 * now files under its topic and industries like any other template, and the gallery shows
 * Original as a badge and a collection. The gate refuses `category: original`.
 */
export const ORIGINAL_KEYWORD = "original";
/** The industry that stands for every line of work: a template that names it names no other. */
export const GENERAL_INDUSTRY = "general";
/** The most industries one template carries (`catalog.yaml` `industries`). */
export const TEMPLATE_INDUSTRY_LIMIT = 3;
export const TaxonomyTermSchema = z.strictObject({
    key: ContentKeySchema,
    labels: z.strictObject({
        en: z.string().trim().min(1).max(80),
        es: z.string().trim().min(1).max(80),
    }),
    /** One line of English that says what belongs under the term. */
    definition: z.string().trim().min(1).max(300).optional(),
});
const TAXONOMY_LISTS = ["categories", "industries", "keywords", "capabilities"];
export const CatalogTaxonomySchema = z
    .strictObject({
    /** What work the agent does: the topics. One per template. */
    categories: z.array(TaxonomyTermSchema).min(1).max(40),
    /** Whose work it serves. 1 to 3 per template; `general` only alone. */
    industries: z.array(TaxonomyTermSchema).min(1).max(40),
    /** Authored keywords: what a sidecar's `tags` may name (the collections, `original` among them). */
    keywords: z.array(TaxonomyTermSchema).max(100).default([]),
    /** Derived from agent.yaml when the catalog is read; never authored, here for labels only. */
    capabilities: z.array(TaxonomyTermSchema).max(20).default([]),
})
    .superRefine((taxonomy, context) => {
    for (const list of TAXONOMY_LISTS) {
        const seen = new Set();
        taxonomy[list].forEach((term, index) => {
            if (seen.has(term.key)) {
                context.addIssue({
                    code: "custom",
                    path: [list, index, "key"],
                    message: `duplicate ${list} key "${term.key}"`,
                });
            }
            seen.add(term.key);
        });
    }
    // A sidecar's tags are checked against both lists: allowed as a keyword, refused as a
    // capability. One key in both would be both at once.
    const capabilities = new Set(taxonomy.capabilities.map((term) => term.key));
    taxonomy.keywords.forEach((term, index) => {
        if (capabilities.has(term.key)) {
            context.addIssue({
                code: "custom",
                path: ["keywords", index, "key"],
                message: `keyword "${term.key}" is also a capability key`,
            });
        }
    });
});
//# sourceMappingURL=catalog-taxonomy.js.map